/**
 * ChangelogPanel — main changelog container for the docking layout.
 *
 * Fetches and displays a paginated, filterable feed of semantic change events
 * for the current project. Auto-refreshes every 10 seconds.
 *
 * Includes an ActivityHeatmap showing the rhythm of project activity.
 * Events related to the currently selected node are highlighted.
 */

import { useCallback, useState } from "react";
import { Badge, Box, Button, Flex, ScrollArea, Text } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useChangelog } from "../../hooks/useChangelog";
import { ChangelogEntry } from "./ChangelogEntry";
import { ChangelogFilters } from "./ChangelogFilters";
import { ActivityHeatmap } from "./ActivityHeatmap";

interface Props {
  projectId: string;
}

const PAGE_SIZE = 50;

export function ChangelogPanel({ projectId }: Props) {
  const { currentBranch, selectedNodeIds } = useProvenanceContext();

  const [eventType, setEventType] = useState("__all__");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [offset, setOffset] = useState(0);
  // Heatmap active day filter — drives since/until independently of the date inputs.
  const [heatmapDay, setHeatmapDay] = useState<string | null>(null);

  // Fetch a larger slice without pagination for the heatmap (no date filter applied here).
  const { data: heatmapData } = useChangelog(projectId, currentBranch, { limit: 200 });

  const effectiveSince = heatmapDay ?? (since || undefined);
  const effectiveUntil = heatmapDay
    ? (() => {
        const d = new Date(heatmapDay);
        d.setDate(d.getDate() + 1);
        return d.toISOString().slice(0, 10);
      })()
    : (until || undefined);

  const { data, isLoading } = useChangelog(projectId, currentBranch, {
    limit: PAGE_SIZE,
    offset,
    eventTypes: eventType !== "__all__" ? [eventType] : undefined,
    since: effectiveSince,
    until: effectiveUntil,
  });

  const handleEventTypeChange = useCallback((type: string) => {
    setEventType(type);
    setOffset(0);
  }, []);

  const handleSinceChange = useCallback((val: string) => {
    setSince(val);
    setHeatmapDay(null);
    setOffset(0);
  }, []);

  const handleUntilChange = useCallback((val: string) => {
    setUntil(val);
    setHeatmapDay(null);
    setOffset(0);
  }, []);

  const handleDaySelect = useCallback((date: string | null) => {
    setHeatmapDay(date);
    setSince("");
    setUntil("");
    setOffset(0);
  }, []);

  const total = data?.total ?? 0;
  const events = data?.events ?? [];
  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;

  return (
    <Box p="3" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Flex align="center" justify="end" mb="2">
        <Badge size="1" color="gray">{total} events</Badge>
      </Flex>

      {/* Activity heatmap — shows project rhythm at a glance */}
      <ActivityHeatmap
        events={heatmapData?.events ?? []}
        onDaySelect={handleDaySelect}
        activeDateFilter={heatmapDay}
      />

      <Box mb="3">
        <ChangelogFilters
          eventType={eventType}
          onEventTypeChange={handleEventTypeChange}
          since={since}
          onSinceChange={handleSinceChange}
          until={until}
          onUntilChange={handleUntilChange}
        />
      </Box>

      {isLoading && <Text size="2" color="gray">Loading events...</Text>}

      {!isLoading && events.length === 0 && (
        <Text size="2" color="gray">No events yet. Changes to parameters, imports, and commits will appear here.</Text>
      )}

      <ScrollArea style={{ flex: 1 }}>
        <Flex direction="column" gap="1">
          {events.map((e) => (
            <ChangelogEntry
              key={e.id}
              event={e}
              highlighted={
                selectedNodeIds.size > 0 &&
                e.primary_node_id != null &&
                selectedNodeIds.has(e.primary_node_id)
              }
            />
          ))}
        </Flex>
      </ScrollArea>

      {(hasPrev || hasNext) && (
        <Flex justify="between" align="center" mt="2" pt="2" style={{ borderTop: "1px solid var(--gray-a4)" }}>
          <Button
            size="1"
            variant="soft"
            disabled={!hasPrev}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Text size="1" color="gray">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </Text>
          <Button
            size="1"
            variant="soft"
            disabled={!hasNext}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </Flex>
      )}
    </Box>
  );
}
